/**
 * Edit Provider Profile Page Component
 *
 * Allows providers to edit their existing profile information.
 * Pre-populates form with current profile data and handles updates.
 *
 * @module components/pages/EditProviderProfilePage
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { apiService, getLogger } from '@/services';
import { ProfileUpdateForm } from '@/components/provider-registration/ProfileUpdateForm';
import type { ProfileFormState } from '@/types/registration';
import type { ProfileGetResponse } from '@/types/provider';
import type { ProfileUpdateApiRequest } from '@/types/registration';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import { Layout } from '../layout';
import { BackButton } from '../ui/BackButton';

/**
 * Transform API response to form data format
 */
const transformApiToFormData = (apiData: ProfileGetResponse): Partial<ProfileFormState['values']> => {
  return {
    firstName: apiData.first_name || '',
    lastName: apiData.last_name || '',
    role: apiData.provider_role || 'GP',
    providerNumber: apiData.ahpra_number || '',
    practiceName: apiData.practice_name || '',
    practiceAddress: apiData.practice_address || '',
    bio: apiData.bio || '',
    consent: {
      dataProcessing: apiData.consent?.data_processing ?? true,
      termsOfService: apiData.consent?.terms_of_service ?? true,
      privacyPolicy: apiData.consent?.privacy_policy ?? true,
      biometricProcessing: apiData.consent?.biometric_processing ?? true,
    },
    // Note: photo handling would need additional implementation for existing photos
  };
};

/**
 * Transform form data to API request format
 */
const transformFormToApiData = async (formData: ProfileFormState['values']): Promise<ProfileUpdateApiRequest> => {
  let photoData = undefined;
  if (formData.photo) {
    // Convert File to base64 for API
    const reader = new FileReader();
    const base64String = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(formData.photo!);
    });

    photoData = {
      data: base64String,
      filename: formData.photo.name,
      mime_type: formData.photo.type,
    };
  }

  return {
    first_name: formData.firstName,
    last_name: formData.lastName,
    role: formData.role,
    provider_number: formData.providerNumber,
    practice_name: formData.practiceName,
    practice_address: formData.practiceAddress,
    bio: formData.bio,
    photo: photoData,
    consent: {
      terms_of_service: formData.consent.termsOfService,
      privacy_policy: formData.consent.privacyPolicy,
      data_processing: formData.consent.dataProcessing,
      biometric_processing: formData.consent.biometricProcessing,
    },
  };
};

/**
 * Edit Provider Profile Page Component
 */
export const EditProviderProfilePage: React.FC = () => {
  const logger = getLogger();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Profile state
  const [profile, setProfile] = useState<ProfileGetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileError, setProfileError] = useState<Error | null>(null);

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [initialFormData, setInitialFormData] = useState<Partial<ProfileFormState['values']> | undefined>();

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    setProfileError(null);

    try {
      const profileData = await apiService.provider.getProfile();
      setProfile(profileData);
      setInitialFormData(transformApiToFormData(profileData));
    } catch (error) {
      logger.error('Failed to fetch provider profile:', error);
      setProfileError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load profile on component mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (formData: ProfileFormState['values']) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // We need the current profile data
      if (!profile) {
        throw new Error('Profile data not available');
      }

      // Transform form data to API format
      const apiData = await transformFormToApiData(formData);

      // Update profile using the provider domain
      const response = await apiService.provider.updateProfile(apiData);

      if (response) {
        // Refresh profile data to get updated information
        await fetchProfile();

        // Navigate back to dashboard
        navigate('/provider/dashboard');
      } else {
        setSubmitError('Failed to update profile. Please try again.');
      }
    } catch (error) {
      logger.error('Profile update error:', error);
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [profile, fetchProfile, navigate]);

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen p-6" data-testid="page-container">
          <div className="max-w-2xl mx-auto" data-testid="inner-container">
            <Card className="p-6">
              <div className="animate-pulse space-y-6" data-testid="loading-skeleton" aria-label="Loading profile information">
                <div className="h-8 bg-neutral-300 rounded w-1/2" data-testid="skeleton-title"></div>
                <div className="h-4 bg-neutral-300 rounded w-3/4" data-testid="skeleton-subtitle"></div>
                <div className="space-y-4">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="h-12 bg-neutral-300 rounded" data-testid={`skeleton-field-${i}`}></div>
                  ))}
                </div>
                <div className="flex gap-4">
                  <div className="h-10 bg-neutral-300 rounded w-24" data-testid="skeleton-button-1"></div>
                  <div className="h-10 bg-neutral-300 rounded w-32" data-testid="skeleton-button-2"></div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (profileError) {
    return (
      <Layout>
        <div className="min-h-screen p-6" data-testid="page-container">
          <div className="max-w-2xl mx-auto" data-testid="inner-container">
            <Card className="p-6">
              <div className="text-center space-y-4">
                <div className={`p-4 rounded-lg ${
                  isDark ? 'bg-error-900/20 text-error-400' : 'bg-error-50 text-error-600'
                }`} data-testid="error-container">
                  <TitleText level={3} className="mb-2">
                    Unable to Load Profile
                  </TitleText>
                  <BodyText>
                    {profileError.message || 'Failed to load your profile information.'}
                  </BodyText>
                </div>
                <div className="flex gap-4 justify-center">
                  <Button variant="outline" onClick={() => fetchProfile()}>
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={handleCancel}>
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // No profile data
  if (!profile || !initialFormData) {
    return (
      <Layout>
        <div className="min-h-screen p-6">
          <div className="max-w-2xl mx-auto">
            <Card className="p-6">
              <div className="text-center space-y-4">
                <TitleText level={3}>
                  Profile Not Found
                </TitleText>
                <BodyText variant="muted">
                  No profile information was found. Please complete your registration first.
                </BodyText>
                <Button variant="outline" onClick={handleCancel}>
                  Back to Dashboard
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <div className="flex items-center gap-4 mb-4">
            <BackButton fallback='/dashboard'/>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <TitleText level={1} className="mb-2">
              Edit Provider Profile
            </TitleText>
            <BodyText variant="secondary">
              Update your professional information and contact details
            </BodyText>
          </div>

          {/* Form Card */}
          <Card className="p-6" data-testid="form-card">
            <ProfileUpdateForm
              providerId={profile.id}
              onSubmit={handleSubmit}
              isLoading={isSubmitting}
              initialData={initialFormData}
              submitError={submitError}
              mode='edit'
            />
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default EditProviderProfilePage;